<template>
  <Modal ref="modalRef" :ok-disabled="!canAddProfileRef" @shown="onShown" @ok="onCreateProfile">
    <template #title>
      <slot v-if="$slots.title" name="title"></slot>
      <template v-else>Add a profile</template>
    </template>
    <div v-if="$slots.instructions">
      <slot name="instructions"></slot>
    </div>
    <label class="form-label given">
      <div>Given name</div>
      <input type="text" id="givenInput" ref="givenInputRef" :disabled="busyRef" class="form-control" v-model="givenRef"/>
    </label>
    <label class="form-label surname">
      <div>Surname</div>
      <input type="text" id="surnameInput" :disabled="busyRef" class="form-control" v-model="surnameRef"/>
    </label>
    <div class="help-text">
      This profile will only exist online.
    </div>
    <div class="add-relationship">
      <label class="form-check-label">
        <input type="checkbox" class="form-check-input" v-model="addRelationshipRef" />
        <template v-if="addRelationshipRef">
          Add as a
          <ProfileRelationshipSelector v-model="newRelationshipRef"/>
          to:
        </template>
        <template v-else>
          Add a relationship to an existing person
        </template>
      </label>
      <template v-if="addRelationshipRef">
        <PersonSelector v-if="relatedItemTypeRef == ItemType.Person"
          v-model="relatedItemIdRef"
          :placeholder="relatedItemSearchPlaceholderRef"
          :post-filter="getSelectableRelatedPersonsAsync"
          :disabled="busyRef"/>
        <FamilySelector v-else
          v-model="relatedItemIdRef"
          :placeholder="relatedItemSearchPlaceholderRef"
          :disabled="busyRef"/>
      </template>
    </div>
    <template #okText>
      Add profile
    </template>
  </Modal>
</template>

<style lang="scss" scoped>
.title-icon {
  margin-right: 0.25rem;
  width: 2.5rem;
  filter: brightness(0) saturate(100%) invert(63%) sepia(76%) saturate(5097%) hue-rotate(184deg) brightness(100%) contrast(94%);
}

:deep(.modal-body) {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding-left: 2rem;
}

.given, .surname {
  display: flex;
  flex-direction: column;

  @media (min-width: 768px) {
    input {
      width: 300px;
    }
  }
}

.add-relationship {
  margin: 1.5rem 0 1rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start; // avoid magic label space
  gap: 0.5rem;

  & > label {
    min-height: 34px;
    display: flex;
    align-items: center;
    gap: 0.5rem;

    input {
      margin: 0;
    }
  }

  .person-selector, .family-selector {
    margin-left: 1.5rem;
    
    @media (min-width: 768px) {
      width: 350px;
    }
  }
}
</style>

<script lang="ts" setup>
import { computed, nextTick, ref, watch } from 'vue'
import { createProfilePersonAsync, getNewProfileRelationshipGender, getNewProfileSearchRelatedPlaceholder, getOrCreateChildRelationshipAsync, getOrCreateProfileFamilyAsync, getOrCreateSpouseRelationshipAsync, isChildRelationship, isParentRelationship, isSpouseRelationship, NewProfileRelationship } from './ProfileManager'
import { FamilyRelationshipRole, ItemType, ViewPerson } from '@/rd/ResearchDataModel'
import Modal from '@/util/Modal.vue'
import ProfileRelationshipSelector from '../profiles/ProfileRelationshipSelector.vue'
import PersonSelector from '../PersonSelector.vue'
import FamilySelector from '../FamilySelector.vue'
import { CompositeId } from '@/rd/CompositeId'
import { useDataGroupStore } from '@/gp/DataGroupStore'
import { useTokenManager } from '@/auth/TokenManager'
import { LoadMode } from '@/util/AsyncData'

const props = defineProps<{
  title?: string
}>()

const emit = defineEmits<{
  (e: 'added', personId: string): void
}>()

defineExpose({
  open: () => modalRef.value?.open()
})

const givenRef = ref('')
const surnameRef = ref('')
const addRelationshipRef = ref(false)
const newRelationshipRef = ref(NewProfileRelationship.Son)
const relatedItemIdRef = ref<string>()
const relatedItemErrorRef = ref<string>()
const busyRef = ref(false)

const modalRef = ref<InstanceType<typeof Modal>>()
const givenInputRef = ref<HTMLInputElement>()

const dataGroupStore = useDataGroupStore()
const tokenManager = useTokenManager()

const hasNameRef = computed(() => !!givenRef.value.trim() || !!surnameRef.value.trim())
const relatedItemTypeRef = computed(() => isChildRelationship(newRelationshipRef.value) ? ItemType.Family : ItemType.Person)
const relatedItemSearchPlaceholderRef = computed(() => getNewProfileSearchRelatedPlaceholder(newRelationshipRef.value))
const canAddProfileRef = computed(() => hasNameRef.value && (!addRelationshipRef.value || (!!relatedItemIdRef.value && !relatedItemErrorRef.value)))

watch(newRelationshipRef, () => relatedItemIdRef.value = undefined)

watch([newRelationshipRef, relatedItemIdRef], async () => {
  relatedItemErrorRef.value = ''

  if (!relatedItemIdRef.value)
    return // obviously invalid

  if (isParentRelationship(newRelationshipRef.value)) {
    const relatedGroupId = CompositeId.getGroupId(relatedItemIdRef.value)
    const relatedGroup = await dataGroupStore.getAsyncGroup(relatedGroupId, LoadMode.EnsureLoaded)?.whenLoadCompleted
    if (relatedGroup?.ownerId != tokenManager.current.userId) {
      relatedItemErrorRef.value = "You don't have permission to create a new parent for this profile."
      return
    }
  }
})

async function onShown() {
  givenRef.value = ''
  surnameRef.value = ''
  addRelationshipRef.value = false
  newRelationshipRef.value = NewProfileRelationship.Son
  relatedItemIdRef.value = undefined
  relatedItemErrorRef.value = ''

  await nextTick()
  givenInputRef.value?.focus()
}

async function getSelectableRelatedPersonsAsync(vps: ViewPerson[]) {
  return vps.filter(vp => !vp.assumeDeceased)
}

async function onCreateProfile() {
  busyRef.value = true

  try {
    const np = {
      gender: addRelationshipRef.value ? getNewProfileRelationshipGender(newRelationshipRef.value) : undefined,
      givenName: givenRef.value.trim(),
      surname: surnameRef.value.trim(),
    }
    const newProfilePersonId = await createProfilePersonAsync(np)

    if (addRelationshipRef.value) {
      await createRelationshipAsync(newProfilePersonId)
    }

    await modalRef.value?.close()
    emit('added', newProfilePersonId)
  }
  finally {
    busyRef.value = false
  }
}

async function createRelationshipAsync(profilePersonId: string) {
  if (!relatedItemIdRef.value)
    throw 'Cannot create profile because no related person or family selected'

  if (isChildRelationship(newRelationshipRef.value)) {
    if (relatedItemTypeRef.value != ItemType.Family)
      throw 'Related item when creating a child profile must be a family'

    await getOrCreateChildRelationshipAsync(profilePersonId, relatedItemIdRef.value)
  }
  else {
    if (relatedItemTypeRef.value != ItemType.Person)
      throw 'Related item when creating a spouse or parent profile must be a person'

    if (isSpouseRelationship(newRelationshipRef.value)) {
      const spouseRole = newRelationshipRef.value == NewProfileRelationship.Husband 
        ? FamilyRelationshipRole.Father
        : FamilyRelationshipRole.Mother
      await getOrCreateSpouseRelationshipAsync(relatedItemIdRef.value, profilePersonId, spouseRole)
    }
    else {
      // create family in (new) parent profile group
      const profileGroupId = CompositeId.getGroupId(profilePersonId)!
      const fatherId = newRelationshipRef.value == NewProfileRelationship.Father ? profilePersonId : undefined
      const motherId = newRelationshipRef.value == NewProfileRelationship.Mother ? profilePersonId : undefined
      const profileFamily = await getOrCreateProfileFamilyAsync(profileGroupId, fatherId, motherId)

      // create child relationship in child profile group
      await getOrCreateChildRelationshipAsync(relatedItemIdRef.value, profileFamily.id!)
    }
  }
}
</script>
